import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';

// Hooks
import { useRFPData } from './hooks/useRFPData';
import { useRFPUpdates } from './hooks/useRFPUpdates';
import { useContentEditor } from './hooks/useContentEditor';
import { useAIFeatures } from './hooks/useAIFeatures';
import { useVersionHistory } from './hooks/useVersionHistory';
import { useRealTimeChat } from '../../../chat/context/RealTimeChatContext';
import { useCollaboration, getUserColor, getUserInitials } from '../../team-member/editor/useCollaboration';

// Layout Components
import Sidebar from './components/Sidebar/Sidebar';
import ContentEditor from '../../team-member/editor/ContentEditor';
import KeyInformation from './components/KeyInformation/KeyInformation';
import GlobalSubmissionActions from './components/Footer/GlobalSubmissionActions';
import RFPAnalysisHeader from './components/RFPAnalysisHeader';
import InfoCardsRow from './components/InfoCardsRow';
import RFPOverlays from './components/RFPOverlays';
import TeamMemberDeadlineBar from './components/TeamMemberDeadlineBar';
import RFPModalsGroup from './components/RFPModalsGroup';
import SuccessToast from '../../../../shared/components/ui/SuccessToast';

// Utils & Constants
import { ArrowLeft } from 'lucide-react';
import GoNoGoResults from '../../shared/components/GoNoGoResults';
import '../../shared/RFPIntake.css';
import api from '../../../../services/api';
import { getActiveStep, stripPrefix, formatForDateInput, formatCurrency } from './utils/helperUtils';
import AnalysisSkeleton from './components/Skeletons/AnalysisSkeleton';
import './RFPAnalysisResult.css';
import '../../shared/components/VersionSidebar.css';


const RFPAnalysisResult = () => {
    const { rfpId, secId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { rfpData: stateData } = location.state || {};

    // ── UI State ───────────────────────────────────────────────────────────
    const [successToast, setSuccessToast] = useState(null);
    const [isJustApproved, setIsJustApproved] = useState(false);
    const [activeSection, setActiveSection] = useState('');
    const [isKeyInfoModalOpen, setIsKeyInfoModalOpen] = useState(false);
    const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
    const [isDocInfoModalOpen, setIsDocInfoModalOpen] = useState(false);
    const [showAIModal, setShowAIModal] = useState(false);
    const [showExport, setShowExport] = useState(false);
    const [stageModal, setStageModal] = useState({ show: false, type: 'Accept', notes: '' });
    const [isMetadataReviewed, setIsMetadataReviewed] = useState(false);
    const [alertModal, setAlertModal] = useState({ show: false, message: '' });
    const [hoveredTabDetails, setHoveredTabDetails] = useState(null);
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
    const [showPPTModal, setShowPPTModal] = useState(false);
    const [isGeneratingSlides] = useState(false);

    // ── Extension & Decision State ────────────────────────────────────────
    const toDMY = (dateStr) => {
        if (!dateStr) return '';
        const parts = dateStr.split('T')[0].split(/[-/]/);
        if (parts.length === 3) {
            if (parts[0].length === 4) return `${parts[2]}-${parts[1]}-${parts[0]}`;
            return `${parts[0]}-${parts[1]}-${parts[2]}`;
        }
        return dateStr;
    };

    const [isExtensionModalOpen, setIsExtensionModalOpen] = useState(false);
    const [extensionDate, setExtensionDate] = useState('');
    const [extensionNotes, setExtensionNotes] = useState('');
    const [isDecisionModalOpen, setIsDecisionModalOpen] = useState(false);
    const [decisionSection, setDecisionSection] = useState(null);
    const [decisionDate, setDecisionDate] = useState('');

    const handleExtensionClick = (section) => {
        setDecisionSection(section);
        setIsDecisionModalOpen(true);
        const requested = section.extension_request?.employee_requested_deadline;
        if (requested) setDecisionDate(toDMY(requested));
    };

    const handleGeneratePPT = async () => setShowPPTModal(true);

    // ── Document & Assignment State ────────────────────────────────────────
    const [documents, setDocuments] = useState([]);
    const [assignments, setAssignments] = useState({});
    const [documentAssignments, setDocumentAssignments] = useState({});
    const blockEditorRef = useRef(null);

    // ── Role Detection ─────────────────────────────────────────────────────
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const userDesignation = (user?.designation || '').toLowerCase();
    const userAuthRole = (user?.role || '').toLowerCase();
    const checkRole = (role) => {
        if (!role) return false;
        const r = String(role).toLowerCase();
        return r.includes('lead') || r.includes('manager') || r.includes('admin') || r.includes('reviewer');
    };
    const isManager = checkRole(userDesignation) || checkRole(userAuthRole);
    const isTeamMember = !isManager;

    // ── Go/No-Go Decision States (Status 12 Flow) ──────────────────────────
    const [isGoNoGoPending, setIsGoNoGoPending] = useState(() => {
        const stateCode = location.state?.globalStatusCode || location.state?.analysisData?.global_status?.code;
        return Number(stateCode) === 12;
    });
    const [fetchingGoNoGo, setFetchingGoNoGo] = useState(false);
    const [goNoGoData, setGoNoGoData] = useState(null);
    const [goNoGoActiveTab, setGoNoGoActiveTab] = useState('client-info');
    const [goNoGoUpdatingDecision, setGoNoGoUpdatingDecision] = useState(false);
    const [goNoGoSelectedDecision, setGoNoGoSelectedDecision] = useState(null);
    const [goNoGoError, setGoNoGoError] = useState(null);

    const { editableData, setEditableData, teamMembers, assignedUsers, fetchTeamMembers, isInitialLoading, sidebarSections, fetchRFPData, fetchOverview, fetchAssignedUsersData } = useRFPData(rfpId, stateData, location.state, isManager, setAssignments);

    const selectedSection = useMemo(() => {
        if (!sidebarSections || sidebarSections.length === 0) return null;
        if (secId) return sidebarSections.find(s => s.id === secId || String(s.id) === String(secId)) || sidebarSections[0];
        return sidebarSections[0];
    }, [secId, sidebarSections]);

    useEffect(() => {
        if (selectedSection) {
            const nextKey = selectedSection.label || selectedSection.key;
            if (nextKey !== activeSection) setActiveSection(nextKey);
        }
    }, [selectedSection, activeSection]);

    useEffect(() => {
        if (editableData?.global_status?.code === 12) {
            setIsGoNoGoPending(true);
        }
    }, [editableData]);

    useEffect(() => {
        if (isGoNoGoPending && rfpId) {
            const fetchGoNoGoDetails = async () => {
                setFetchingGoNoGo(true);
                setGoNoGoError(null);
                try {
                    const res = await api.get(`/extraction/v1/${rfpId}/go-no-go`);
                    setGoNoGoData(res.data);
                } catch (err) {
                    console.error('Error fetching go-no-go:', err);
                    setGoNoGoError('Failed to load Go/No-Go assessment data.');
                } finally {
                    setFetchingGoNoGo(false);
                }
            };
            fetchGoNoGoDetails();
        }
    }, [isGoNoGoPending, rfpId]);

    // Unpack WebSocket real-time updates
    const { lastMessage } = useRealTimeChat();

    // Listen for WebSocket events to synchronize the active proposal workspace in real-time
    useEffect(() => {
        if (!lastMessage) return;

        const { type, payload } = lastMessage;

        const WORKSPACE_SYNC_EVENTS = [
            'SECTION_SUBMITTED',
            'SECTION_UPDATED',
            'SECTION_RETURNED',
            'SECTION_APPROVED',
            'SECTION_REASSIGNED',
            'RFP_ASSIGNED',
            'NOTIFICATION_RECEIVED',
            'NOTIFICATION'
        ];

        const resolvedType = type || lastMessage.event;

        if (WORKSPACE_SYNC_EVENTS.includes(resolvedType)) {
            const eventRfpId = payload?.rfpId || payload?.rfp_id || payload?.data?.rfpId || payload?.data?.rfp_id || payload?.notification?.rfp_id;

            // Only refetch if the event affects the currently active proposal
            if (eventRfpId && String(eventRfpId) === String(rfpId)) {
                console.log(`✅ [WS REFRESH] Synchronizing active workspace (${rfpId}) due to: ${resolvedType}`);
                if (typeof fetchRFPData === 'function') fetchRFPData(true);
                if (typeof fetchOverview === 'function') fetchOverview();
                if (typeof fetchAssignedUsersData === 'function') fetchAssignedUsersData();
            }
        }
    }, [lastMessage, rfpId, fetchRFPData, fetchOverview, fetchAssignedUsersData]);

    const handleGoNoGoSubmit = (stageCode) => {
        setAlertModal({
            show: true,
            message: stageCode === 8
                ? "Are you sure you want to move the proposal forward for further preparation and submission activities?"
                : "Are you sure you want to discontinue further proposal activities?",
            stageCode: stageCode,
            isConfirmation: true
        });
    };

    const handleConfirmGoNoGo = async () => {
        const { stageCode } = alertModal;
        setGoNoGoSelectedDecision(stageCode);
        setGoNoGoUpdatingDecision(true);
        try {
            await api.patch(`/rfp/${rfpId}/proposal-stage`, { stage: stageCode, notes: "" });
            setAlertModal({ show: false, message: '', stageCode: null });
            if (stageCode === 8) {
                setIsGoNoGoPending(false);
                if (location.state) {
                    location.state.globalStatusCode = null;
                    if (location.state.analysisData) {
                        location.state.analysisData.global_status.code = 8;
                    }
                }
                await fetchRFPData();
            } else {
                navigate('/proposals');
            }
        } catch (err) {
            console.error('Error submitting Go/No-Go decision:', err);
            setAlertModal({ show: true, message: "Failed to update Go/No-Go decision." });
        } finally {
            setGoNoGoUpdatingDecision(false);
            setGoNoGoSelectedDecision(null);
        }
    };

    const sectionKeys = useMemo(() => sidebarSections.map(s => s.label || s.key), [sidebarSections]);

    const { showVersionHistory, setShowVersionHistory, versionsList, activeVersionMenuId, setActiveVersionMenuId, isFetchingVersionDetails, fetchVersions, handleViewVersion, fetchComparison, comparisonData, showComparisonModal, setShowComparisonModal, isSelectingCompareFromView, setIsSelectingCompareFromView, compareSourceVersion, setCompareSourceVersion, isFetchingComparison, showLabelModal, setShowLabelModal, setLabelTargetVersion, versionLabelText, setVersionLabelText, handleRenameVersion, showRevertModal, setShowRevertModal, revertTargetVersion, setRevertTargetVersion, revertReason, setRevertReason, handleRevertVersion, showVersionDetailsModal, setShowVersionDetailsModal, selectedVersionData, setSelectedVersionData } = useVersionHistory(rfpId, activeSection, setEditableData, editableData);

    // Refs so callbacks (defined after the hook) can be passed as stable closures
    const onSaveSuccessRef = useRef(null);

    const { isUpdating, isStageUpdating, updateStatus, setUpdateStatus, stageError, setStageError, executeStageTransition, handleUpdate, handleToggleSectionApproval, handleSubmitToLead, handleClarificationRequest, handleUpdateMetadata, handleRequestExtension, handleExtensionDecision, isSubmittingToLead, isApprovingSection } = useRFPUpdates(
        rfpId, editableData, setEditableData, setActiveSection, setAlertModal, isManager,
        activeSection, fetchRFPData, fetchOverview, setAssignments, fetchVersions,
        (saveInfo) => { if (onSaveSuccessRef.current) onSaveSuccessRef.current(saveInfo); }
    );


    // Extension approval & Section reassignment toast — runs AFTER useRFPUpdates so setUpdateStatus is defined
    useEffect(() => {
        if (!lastMessage) return;
        const { type, payload } = lastMessage;

        // Match standard notification type or generic notification wrappers
        const isNotification = type === 'NOTIFICATION_RECEIVED' || type === 'NOTIFICATION' || String(lastMessage.event).toUpperCase() === 'NOTIFICATION';
        const isAssignment = type === 'RFP_ASSIGNED' || type === 'SECTION_REASSIGNED' || String(lastMessage.event).toUpperCase() === 'RFP_ASSIGNED' || String(lastMessage.event).toUpperCase() === 'SECTION_REASSIGNED';

        if (isNotification || isAssignment) {
            const eventRfpId = payload?.rfpId || payload?.rfp_id || payload?.data?.rfpId || payload?.data?.rfp_id || payload?.notification?.rfp_id;

            if (eventRfpId && String(eventRfpId) === String(rfpId)) {
                const msg = (
                    payload?.message ||
                    payload?.title ||
                    payload?.data?.message ||
                    payload?.data?.title ||
                    payload?.notification?.message ||
                    ''
                ).toLowerCase();

                if (msg.includes('extension') && msg.includes('approved')) {
                    const newDeadline = payload?.deadline || payload?.data?.deadline || payload?.notification?.deadline || payload?.data?.new_deadline;

                    console.log('🎉 [WS] Extension Approved notification received! Triggering UI state shifts.');

                    // 1. Refresh section details automatically
                    if (typeof fetchRFPData === 'function') fetchRFPData(true);
                    if (typeof fetchOverview === 'function') fetchOverview();
                    if (typeof fetchAssignedUsersData === 'function') fetchAssignedUsersData();

                    // 2. Display success banner alert notification
                    setSuccessToast({
                        message: '✓ Extension Approved',
                        rfpName: `Your section deadline has been updated to ${newDeadline ? toDMY(newDeadline) : '15-06-2026'}.`
                    });
                    setTimeout(() => setSuccessToast(null), 5000);

                    // 3. Trigger visual success glow/pulse highlight inside deadline card
                    setIsJustApproved(true);
                    setTimeout(() => setIsJustApproved(false), 5000);
                } else if (isAssignment || msg.includes('assign') || msg.includes('reassign')) {
                    console.log('🎉 [WS] Section Reassignment notification received! Triggering UI state shifts.');

                    // 1. Refresh section details automatically
                    if (typeof fetchRFPData === 'function') fetchRFPData(true);
                    if (typeof fetchOverview === 'function') fetchOverview();
                    if (typeof fetchAssignedUsersData === 'function') fetchAssignedUsersData();

                    // 2. Display success toast notification
                    let detailsText = 'Section assignments have been updated.';
                    const rawMsgObj = payload?.message || payload?.data?.message || payload?.notification?.message || payload;
                    if (rawMsgObj && typeof rawMsgObj === 'object') {
                        const sec = rawMsgObj.section || rawMsgObj.section_title || rawMsgObj.title || '';
                        const usr = rawMsgObj.assigned_to || rawMsgObj.assigned_user || '';
                        if (sec && usr) {
                            detailsText = `Section: ${sec} assigned to ${usr}`;
                        } else if (rawMsgObj.body || rawMsgObj.content || rawMsgObj.text) {
                            detailsText = rawMsgObj.body || rawMsgObj.content || rawMsgObj.text;
                        }
                    } else if (typeof rawMsgObj === 'string') {
                        detailsText = rawMsgObj;
                    }

                    // Strip any leading checkmarks from message
                    const cleanMsg = detailsText.replace(/^✓\s*/, '');

                    setSuccessToast({
                        message: '✓ Section Reassigned Successfully',
                        rfpName: cleanMsg
                    });
                    setTimeout(() => setSuccessToast(null), 5000);
                }
            }
        }
    }, [lastMessage, rfpId, fetchRFPData, fetchOverview, fetchAssignedUsersData]);

    const {
        selectedCell, setSelectedCell, localBlocks, setLocalBlocks,
        isEditingContent, setIsEditingContent,
        handleBlockEdit, handleTableCellEdit, handleSidePanelEdit,
        handleTableAction, handleRenameColumn, cancelEditing,
        addToHistory, undo, redo, startEditing,
        originalContent, applyRemoteContent,
    } = useContentEditor(activeSection, editableData, setEditableData);

    // ── Fetch latest saved content for a specific section from the backend ──────────
    const fetchLatestSectionContent = useCallback(async (sectionKey) => {
        if (!rfpId || !sectionKey) return null;
        try {
            const endpoint = isManager
                ? `/rfp/${rfpId}`
                : `/rfp/employee/${rfpId}/my-sections`;
            const response = await api.get(endpoint);
            const freshData = response.data;
            if (freshData && freshData[sectionKey] !== undefined) return freshData[sectionKey];
            const secs = freshData?.sections || [];
            const sec = secs.find(s => s.label === sectionKey || s.key === sectionKey);
            if (sec && sec.content !== undefined) return sec.content;
            return null;
        } catch (err) {
            console.warn('[fetchLatestSectionContent] Failed:', err);
            return null;
        }
    }, [rfpId, isManager]);

    // ── Section save success: toast for the saving user, update ref ─────────────────
    const handleSectionSaveSuccess = useCallback(({ sectionKey, savedByName }) => {
        // No-op to avoid duplicate toast for the saving user (backend response toast is shown instead)
    }, []);

    // Keep the ref current so the closure in useRFPUpdates always has the latest
    useEffect(() => { onSaveSuccessRef.current = handleSectionSaveSuccess; }, [handleSectionSaveSuccess]);

    // ── SECTION_CONTENT_SAVED / SECTION_UPDATED: sync peers ────────────────────────
    // This effect handles the incoming WS event emitted by the backend AFTER any user saves.
    // It decides whether to: auto-refresh, show a conflict prompt, or silently update.
    useEffect(() => {
        if (!lastMessage) return;
        const resolvedType = lastMessage.type || lastMessage.event || '';
        const isSectionSaved = [
            'SECTION_CONTENT_SAVED', 'SECTION_UPDATED', 'SECTION_SUBMITTED'
        ].includes(resolvedType.toUpperCase());
        if (!isSectionSaved) return;

        const payload = lastMessage.payload || lastMessage.data || lastMessage;
        const eventRfpId = payload?.rfp_id || payload?.rfpId;
        const eventSectionKey = payload?.section_key || payload?.sectionKey || payload?.section_id;
        const savedByName = payload?.saved_by || payload?.saved_by_name || 'A collaborator';

        // Only apply to the currently open RFP
        if (eventRfpId && String(eventRfpId) !== String(rfpId)) return;

        // Only apply if the event is about the currently active section
        const isCurrentSection = !eventSectionKey || eventSectionKey === activeSection;

        if (!isCurrentSection) {
            // Another section was updated — just silently refresh full data
            if (typeof fetchRFPData === 'function') fetchRFPData(true);
            return;
        }

        const hasLocalUnsavedChanges =
            isEditingContent &&
            originalContent !== null &&
            editableData[activeSection] !== originalContent;

        if (hasLocalUnsavedChanges) {
            // Warn the user — do NOT overwrite their unsaved draft
            setSuccessToast({
                message: `⚠️ New saved changes from ${savedByName}`,
                rfpName: 'New changes are available. Cancel your edits to load the latest saved content.'
            });
            setTimeout(() => setSuccessToast(null), 7000);
        } else if (isEditingContent) {
            // In edit mode but no local changes — silently apply fresh content
            fetchLatestSectionContent(activeSection).then(freshContent => {
                if (freshContent !== null) {
                    applyRemoteContent(activeSection, freshContent);
                    setSuccessToast({
                        message: `✓ Updated by ${savedByName}`,
                        rfpName: `Section content has been refreshed.`
                    });
                    setTimeout(() => setSuccessToast(null), 4000);
                }
            });
        } else {
            // In view mode — auto-update immediately
            fetchLatestSectionContent(activeSection).then(freshContent => {
                if (freshContent !== null) {
                    applyRemoteContent(activeSection, freshContent);
                    setSuccessToast({
                        message: `✓ Section updated by ${savedByName}`,
                        rfpName: `The section was updated just now.`
                    });
                    setTimeout(() => setSuccessToast(null), 4000);
                }
            });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastMessage]);

    // ── SECTION_CONTENT_REVERTED: global force-refresh for all peers ─────────────
    // Fires when the Team Lead issues a revert. ALL users on the same rfp+section
    // MUST immediately discard local drafts and display the restored baseline content.
    useEffect(() => {
        if (!lastMessage) return;
        const resolvedType = String(lastMessage.type || lastMessage.event || '').toUpperCase();
        if (resolvedType !== 'SECTION_CONTENT_REVERTED') return;

        const payload = lastMessage.payload || lastMessage.data || lastMessage;
        const eventRfpId = payload?.rfp_id || payload?.rfpId;
        const eventSectionKey = payload?.section_key || payload?.sectionKey || payload?.section_id;
        const revertedBy = payload?.reverted_by || payload?.revertedBy || 'Team Lead';
        const restoredContent = payload?.content ?? null;

        // Only act on our own RFP
        if (eventRfpId && String(eventRfpId) !== String(rfpId)) return;

        const isCurrentSection = !eventSectionKey || eventSectionKey === activeSection;

        const applyRevert = async () => {
            if (restoredContent !== null) {
                // Apply the content shipped with the WS event (no extra HTTP round-trip needed)
                applyRemoteContent(activeSection, restoredContent);
            } else {
                // Fallback: re-fetch from backend
                const freshContent = await fetchLatestSectionContent(activeSection);
                if (freshContent !== null) applyRemoteContent(activeSection, freshContent);
            }
            // Force-exit edit mode so the user sees the reverted content
            cancelEditing();
            setSuccessToast({
                message: `↩ Content reverted by ${revertedBy}`,
                rfpName: 'Section content was restored to the original baseline by the Team Lead.'
            });
            setTimeout(() => setSuccessToast(null), 6000);
        };

        if (isCurrentSection) {
            applyRevert();
        } else {
            // Another section was reverted — just silently refresh so sidebar status syncs
            if (typeof fetchRFPData === 'function') fetchRFPData(true);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastMessage]);

    // ── Cancel with fetch — discard local draft, load latest saved content ──────────
    // This replaces the old cancelEditing reference in ContentEditor so Cancel never
    // restores a stale snapshot from when Edit was first clicked; it always shows the
    // most recently saved backend content.
    const handleCancelEditing = useCallback(async () => {
        const latestContent = await fetchLatestSectionContent(activeSection);
        cancelEditing(latestContent !== null ? latestContent : undefined);
        if (latestContent !== null) {
            setSuccessToast({
                message: '✓ Edit cancelled',
                rfpName: 'Your unsaved changes were discarded. Latest saved content loaded.'
            });
            setTimeout(() => setSuccessToast(null), 4000);
        }
    }, [fetchLatestSectionContent, activeSection, cancelEditing]);


    // ── Global Cancel and Sync ─────────────────────────────────────────────
    // Cancels local edits and syncs the original content to the backend,
    // which broadcasts to all peers to revert to that original content.
    const handleCancelSync = useCallback(async () => {
        try {
            // originalContent comes from useContentEditor
            const contentToSync = originalContent || '';
            
            // Revert local view immediately
            cancelEditing();
            
            // Save it to backend to broadcast
            await handleUpdate(isEditingContent, setIsEditingContent, contentToSync);
            
            // Clear the generic update toast to prevent overlaps
            setUpdateStatus(null);
            
            setSuccessToast({
                message: '✓ Section Synced',
                rfpName: 'Local edits discarded and section synced for all collaborators.'
            });
            setTimeout(() => setSuccessToast(null), 5000);
        } catch (error) {
            console.error('Cancel and sync failed', error);
        }
    }, [originalContent, cancelEditing, handleUpdate, isEditingContent, setIsEditingContent, setUpdateStatus]);

    const { isRefining, aiPrompt, setAiPrompt, handleAIImprove } = useAIFeatures(rfpId, editableData, activeSection, selectedCell, handleSidePanelEdit, setUpdateStatus);

    // ── Collaboration setup (additive – no effect if WS server is unavailable) ──
    const collabSectionId = selectedSection?.id || selectedSection?.key || activeSection || 'default';
    const wsBaseUrl = process.env.REACT_APP_COLLAB_WS_URL || 'wss://demos.yjs.dev/ws';
    const collabUserMeta = useMemo(() => {
        const getCleanName = () => {
            const rawName = user?.employee_name || user?.full_name || user?.name || `${user?.first_name || ''} ${user?.last_name || ''}`.trim();
            if (rawName) return rawName;

            const email = user?.email || 'Collaborator';
            if (email.includes('@')) {
                const part = email.split('@')[0];
                return part
                    .split(/[._-]/)
                    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(' ');
            }
            return email;
        };

        const finalName = getCleanName();
        return {
            id: user?.id || user?.email || '',
            name: finalName,
            color: getUserColor(String(user?.id || user?.email || '')),
            initials: getUserInitials(finalName),
        };
    }, [user?.id, user?.email, user?.name, user?.employee_name, user?.full_name, user?.first_name, user?.last_name]);

    const { ydoc: collabYdoc, provider: collabProvider, status: collabStatus, collaborators, collabReady } =
        useCollaboration({
            rfpId,
            sectionId: collabSectionId,
            user: collabUserMeta,
            wsBaseUrl,
            isEditing: isEditingContent,
        });

    // ── Effects ────────────────────────────────────────────────────────────
    useEffect(() => { setIsEditingContent(false); setUpdateStatus(null); }, [activeSection, setIsEditingContent, setUpdateStatus]);
    useEffect(() => { if (isManager) fetchTeamMembers(); }, [isManager, fetchTeamMembers]);



    const activeStep = getActiveStep(editableData, isManager);

    const allSectionsApproved = useMemo(() => {
        if (!sectionKeys.length) return false;
        return sectionKeys.every(k => (editableData?.SECTION_STATUS?.[k] === 'Approved' || editableData?.section_status?.[k] === 'Approved'));
    }, [sectionKeys, editableData?.SECTION_STATUS, editableData?.section_status]);

    useEffect(() => {
        if (editableData?.SECTION_ASSIGNMENTS) setAssignments(prev => ({ ...prev, ...editableData.SECTION_ASSIGNMENTS }));
    }, [editableData?.SECTION_ASSIGNMENTS]);

    const handleMetadataChange = (key, value) => setEditableData(prev => ({ ...prev, [key]: value, [key.toLowerCase()]: value }));

    const isNoGo = editableData?.global_status?.code === 9 ||
        String(editableData?.proposal_stage || '').toUpperCase() === 'NO_GO' ||
        String(editableData?.proposal_stage || '').toUpperCase() === 'NO-GO' ||
        String(editableData?.proposal_stage || '').toUpperCase() === 'NO GO';

    const isTeamLeadLockedStatus = (globalStatus) => [5, 6, 7].includes(Number(globalStatus));
    const isTeamLeadViewOnly = isManager && isTeamLeadLockedStatus(editableData?.global_status?.code);

    const getTeamLeadViewOnlyMessage = () => {
        const status = Number(editableData?.global_status?.code);
        if (status === 5) return "Editing is disabled because this proposal has been submitted to the client.";
        if (status === 6) return "Editing is disabled because this proposal has been accepted (WON).";
        if (status === 7) return "Editing is disabled because this proposal has been rejected (LOST).";
        return "Editing is disabled because this proposal is already finalized/submitted.";
    };

    const getViewOnlyBannerStyle = () => {
        const status = Number(editableData?.global_status?.code);
        if (status === 6) { // WON
            return {
                backgroundColor: '#ECFDF5',
                border: '1px solid #A7F3D0',
                color: '#047857'
            };
        }
        if (status === 7) { // LOST
            return {
                backgroundColor: '#FEF2F2',
                border: '1px solid #FCA5A5',
                color: '#B91C1C'
            };
        }
        // Default / Status 5 (Submitted)
        return {
            backgroundColor: '#FFF1EC',
            border: '1px solid #FED7AA',
            color: '#E9471D'
        };
    };

    const safeExecuteStageTransition = (...args) => { if (isTeamLeadViewOnly) return; return executeStageTransition(...args); };
    const safeHandleUpdate = (...args) => { if (isTeamLeadViewOnly) return; return handleUpdate(...args); };
    const safeHandleToggleSectionApproval = (...args) => { if (isTeamLeadViewOnly) return; return handleToggleSectionApproval(...args); };
    const safeHandleSubmitToLead = (...args) => { if (isTeamLeadViewOnly) return; return handleSubmitToLead(...args); };
    const safeHandleUpdateMetadata = (...args) => { if (isTeamLeadViewOnly) return; return handleUpdateMetadata(...args); };
    const safeHandleRequestExtension = (...args) => { if (isTeamLeadViewOnly) return; return handleRequestExtension(...args); };
    const safeHandleExtensionDecision = (...args) => { if (isTeamLeadViewOnly) return; return handleExtensionDecision(...args); };
    const safeHandleAIImprove = (...args) => { if (isTeamLeadViewOnly) return; return handleAIImprove(...args); };

    const isAnyModalOpen = !!(
        showExport ||
        showPPTModal ||
        showVersionDetailsModal ||
        showComparisonModal ||
        isKeyInfoModalOpen ||
        isAssignModalOpen ||
        isDocInfoModalOpen ||
        stageModal?.show ||
        showAIModal ||
        showLabelModal ||
        showRevertModal ||
        isDecisionModalOpen ||
        isExtensionModalOpen ||
        alertModal?.show ||
        isGeneratingSlides
    );

    // ── Go/No-Go Decision Screen (Pending 12) ──────────────────────────────
    if (isGoNoGoPending) {
        return (
            <div className="rfp-intake-page" style={{ padding: '2rem', minHeight: '100vh', background: '#F8F9FA' }}>
                <div className="intake-header-refined" style={{ marginBottom: '2rem' }}>
                    <button className="refined-back-btn" onClick={() => navigate('/proposals')}>
                        <ArrowLeft size={20} />
                    </button>
                    <div className="refined-header-text">
                        <h1>Proposal Evaluation</h1>
                        <p>Review the AI-generated assessment insights and determine whether to proceed with the proposal opportunity.</p>
                    </div>
                </div>

                <div className="scanning-container animate-fade-in" style={{ maxWidth: '1000px', margin: '0 auto', background: '#FFFFFF', borderRadius: '6px', boxShadow: '0 4px 20px rgba(0,0,0,0.05)', padding: '0.2rem' }}>
                    {fetchingGoNoGo ? (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '6rem 0', gap: '1.25rem' }}>
                            <div className="animate-spin" style={{ width: '40px', height: '40px', border: '4px solid #E2E8F0', borderTopColor: '#FF5526', borderRadius: '50%' }}></div>
                            <span style={{ fontSize: '14px', color: '#64748B', fontWeight: '500' }}>Fetching Go/No-Go Assessment...</span>
                        </div>
                    ) : goNoGoError ? (
                        <div style={{ textAlign: 'center', padding: '4rem 0', color: '#EF4444' }}>
                            <p style={{ fontWeight: '600', fontSize: '16px' }}>{goNoGoError}</p>
                            <button onClick={() => window.location.reload()} style={{ marginTop: '1.25rem', padding: '8px 16px', background: '#FF5526', color: '#FFFFFF', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' }}>Retry</button>
                        </div>
                    ) : goNoGoData ? (
                        <GoNoGoResults
                            goNoGoData={goNoGoData}
                            activeTab={goNoGoActiveTab}
                            setActiveTab={setGoNoGoActiveTab}
                            handleGoNoGoSubmit={handleGoNoGoSubmit}
                            updatingDecision={goNoGoUpdatingDecision}
                            selectedDecision={goNoGoSelectedDecision}
                        />
                    ) : (
                        <div style={{ textAlign: 'center', padding: '4rem 0', color: '#64748B' }}>
                            No assessment data available.
                        </div>
                    )}
                </div>

                <RFPOverlays
                    updateStatus={updateStatus} alertModal={alertModal} setAlertModal={setAlertModal}
                    onConfirm={handleConfirmGoNoGo}
                    navigate={navigate} showExport={showExport} setShowExport={setShowExport}
                    showPPTModal={showPPTModal} setShowPPTModal={setShowPPTModal}
                    rfpId={rfpId} editableData={editableData}
                    isManager={isManager}
                    showVersionDetailsModal={showVersionDetailsModal}
                    setShowVersionDetailsModal={setShowVersionDetailsModal}
                    selectedVersionData={selectedVersionData} activeSection={activeSection}
                    versionsList={versionsList} setRevertTargetVersion={setRevertTargetVersion}
                    setShowRevertModal={setShowRevertModal} fetchComparison={fetchComparison}
                    handleRenameVersion={handleRenameVersion}
                    showComparisonModal={showComparisonModal} setShowComparisonModal={setShowComparisonModal}
                    comparisonData={comparisonData} hoveredTabDetails={hoveredTabDetails}
                    stripPrefix={stripPrefix} showVersionHistory={showVersionHistory}
                    setShowVersionHistory={setShowVersionHistory} activeVersionMenuId={activeVersionMenuId}
                    setActiveVersionMenuId={setActiveVersionMenuId} handleViewVersion={handleViewVersion}
                    isFetchingVersionDetails={isFetchingVersionDetails}
                    setLabelTargetVersion={setLabelTargetVersion}
                    setVersionLabelText={setVersionLabelText} setShowLabelModal={setShowLabelModal}
                    setIsSelectingCompareFromView={setIsSelectingCompareFromView}
                    isSelectingCompareFromView={isSelectingCompareFromView}
                    compareSourceVersion={compareSourceVersion} setCompareSourceVersion={setCompareSourceVersion}
                    setSelectedVersionData={setSelectedVersionData}
                    isFetchingComparison={isFetchingComparison} isGeneratingSlides={isGeneratingSlides}
                    isAnyModalOpen={isAnyModalOpen}
                />
            </div>
        );
    }

    // ── Loading State ──────────────────────────────────────────────────────
    if (isInitialLoading || !editableData) {
        return <AnalysisSkeleton />;
    }

    return (
        <div className="analysis-result-page">
            {/* All workflow modals in one group */}
            <RFPModalsGroup
                setSuccessToast={setSuccessToast}
                // Key Info
                isKeyInfoModalOpen={isKeyInfoModalOpen} setIsKeyInfoModalOpen={setIsKeyInfoModalOpen}
                handleUpdateMetadata={safeHandleUpdateMetadata} isUpdating={isUpdating}
                editableData={editableData} handleMetadataChange={handleMetadataChange}
                formatForDateInput={formatForDateInput}
                // Section Assign
                isAssignModalOpen={isAssignModalOpen} setIsAssignModalOpen={setIsAssignModalOpen}
                assignments={assignments} setAssignments={setAssignments}
                rfpId={rfpId} teamMembers={teamMembers}
                fetchOverview={fetchOverview} fetchRFPData={fetchRFPData}
                // Document Info
                isDocInfoModalOpen={isDocInfoModalOpen} setIsDocInfoModalOpen={setIsDocInfoModalOpen}
                documents={documents} setDocuments={setDocuments}
                documentAssignments={documentAssignments} setDocumentAssignments={setDocumentAssignments}
                assignedUsers={assignedUsers}
                isTeamLeadViewOnly={[6, 7, 9].includes(Number(editableData?.global_status?.code))}
                // Stage Confirmation
                stageModal={stageModal} setStageModal={setStageModal}
                stageError={stageError} setStageError={setStageError}
                handleClarificationRequest={handleClarificationRequest}
                activeSection={activeSection} executeStageTransition={safeExecuteStageTransition}
                isStageUpdating={isStageUpdating}
                // AI Improvement
                showAIModal={showAIModal} setShowAIModal={setShowAIModal}
                aiPrompt={aiPrompt} setAiPrompt={setAiPrompt}
                handleAIImprove={safeHandleAIImprove} isRefining={isRefining}
                // Rename Version
                showLabelModal={showLabelModal} setShowLabelModal={setShowLabelModal}
                versionLabelText={versionLabelText} setVersionLabelText={setVersionLabelText}
                handleRenameVersion={handleRenameVersion}
                // Revert Version
                showRevertModal={showRevertModal} setShowRevertModal={setShowRevertModal}
                revertTargetVersion={revertTargetVersion} revertReason={revertReason}
                setRevertReason={setRevertReason} handleRevertVersion={handleRevertVersion}
                // Decision
                isDecisionModalOpen={isDecisionModalOpen} setIsDecisionModalOpen={setIsDecisionModalOpen}
                decisionSection={decisionSection} decisionDate={decisionDate}
                setDecisionDate={setDecisionDate} handleExtensionDecision={safeHandleExtensionDecision}
                toDMY={toDMY} isManager={isManager}
                fetchAssignedUsersData={fetchAssignedUsersData}
                // Extension Request
                isExtensionModalOpen={isExtensionModalOpen} setIsExtensionModalOpen={setIsExtensionModalOpen}
                selectedSection={selectedSection} extensionDate={extensionDate}
                setExtensionDate={setExtensionDate} extensionNotes={extensionNotes}
                setExtensionNotes={setExtensionNotes} handleRequestExtension={safeHandleRequestExtension}
            />

            <RFPAnalysisHeader
                isManager={isManager} editableData={editableData} navigate={navigate}
                setIsKeyInfoModalOpen={setIsKeyInfoModalOpen} setShowExport={setShowExport}
                rfpId={rfpId}
                fetchOverview={fetchOverview}
                executeStageTransition={executeStageTransition}
                isStageUpdating={isStageUpdating}
                isNoGo={isNoGo}
                isTeamLeadViewOnly={isTeamLeadViewOnly}
                selectedSection={selectedSection}
                handleSubmitToLead={handleSubmitToLead}
                fetchRFPData={fetchRFPData}
            />

            <style>{`
                @keyframes fadeInDown {
                    from {
                        opacity: 0;
                        transform: translateY(-10px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
            `}</style>

            {successToast && (
                <SuccessToast toast={successToast} />
            )}

            {isTeamLeadViewOnly && (
                <div style={{
                    margin: '1rem 2rem 1rem 2rem',
                    padding: '0.75rem 1rem',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    ...getViewOnlyBannerStyle()
                }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>
                    <span>This proposal is in view-only mode. {getTeamLeadViewOnlyMessage()}</span>
                </div>
            )}

            <InfoCardsRow activeStep={activeStep} editableData={editableData} isManager={isManager} />

            {/* Team Member Deadline Bar: between KPI cards and Editor */}
            <TeamMemberDeadlineBar
                isManager={isManager} selectedSection={selectedSection}
                editableData={editableData} toDMY={toDMY}
                onRequestExtension={() => setIsExtensionModalOpen(true)}
                isNoGo={isNoGo}
                isJustApproved={isJustApproved}
            />

            <div className={`final-review-workspace redesigned-workspace ${!isManager ? 'member-redesigned-workspace' : ''}`}>
                <Sidebar
                    sidebarSections={sidebarSections} activeSection={activeSection}
                    setActiveSection={setActiveSection} editableData={editableData}
                    isManager={isManager} setIsAssignModalOpen={setIsAssignModalOpen}
                    setIsDocInfoModalOpen={setIsDocInfoModalOpen} assignments={assignments}
                    stripPrefix={stripPrefix} rfpId={rfpId} navigate={navigate}
                    setHoveredTabDetails={setHoveredTabDetails}
                    isSidebarCollapsed={isSidebarCollapsed} fetchOverview={fetchOverview}
                    fetchAssignedUsersData={fetchAssignedUsersData}
                    onExtensionClick={handleExtensionClick}
                    isNoGo={isNoGo}
                    isTeamLeadViewOnly={isTeamLeadViewOnly}
                />

                <ContentEditor
                    activeSection={activeSection} selectedSection={selectedSection}
                    editableData={editableData} activeStep={activeStep}
                    isEditingContent={isEditingContent} setIsEditingContent={setIsEditingContent}
                    isUpdating={isUpdating}
                    handleUpdate={() => safeHandleUpdate(isEditingContent, setIsEditingContent)}
                    startEditing={startEditing} setShowAIModal={setShowAIModal}
                    setIsAssignModalOpen={setIsAssignModalOpen}
                    selectedCell={selectedCell} setSelectedCell={setSelectedCell}
                    localBlocks={localBlocks} setLocalBlocks={setLocalBlocks}
                    handleBlockEdit={handleBlockEdit} handleTableCellEdit={handleTableCellEdit}
                    handleSidePanelEdit={handleSidePanelEdit} handleTableAction={handleTableAction}
                    handleRenameColumn={handleRenameColumn} cancelEditing={handleCancelEditing}
                    addToHistory={addToHistory} undo={undo} redo={redo}
                    renderKeyInformation={() => (
                        <KeyInformation
                            editableData={editableData}
                            valuationDisplayVal={formatCurrency(editableData.VALUATION || editableData.valuation, editableData.CURRENCY || editableData.currency)}
                            valuationVal={editableData.VALUATION || editableData.valuation}
                        />
                    )}
                    isManager={isManager} setStageModal={setStageModal} setStageError={setStageError}
                    handleToggleSectionApproval={safeHandleToggleSectionApproval}
                    handleSubmitToLead={() => safeHandleSubmitToLead(activeSection, fetchRFPData)}
                    isSubmittingToLead={isSubmittingToLead} isApprovingSection={isApprovingSection} blockEditorRef={blockEditorRef}
                    setShowVersionHistory={setShowVersionHistory}
                    isSidebarCollapsed={isSidebarCollapsed} setIsSidebarCollapsed={setIsSidebarCollapsed}
                    assignments={assignments}
                    rfpId={rfpId}
                    fetchRFPData={fetchRFPData}
                    isNoGo={isNoGo}
                    isTeamLeadViewOnly={isTeamLeadViewOnly}
                    collabYdoc={collabYdoc}
                    collabProvider={collabProvider}
                    collabUser={collabUserMeta}
                    collabStatus={collabStatus}
                    collaborators={collaborators}
                    collabReady={collabReady}
                    onCancelSync={handleCancelSync}
                />
            </div>

            <GlobalSubmissionActions
                activeStep={activeStep} isTeamMember={isTeamMember} isManager={isManager}
                allSectionsApproved={allSectionsApproved} isMetadataReviewed={isMetadataReviewed}
                setIsMetadataReviewed={setIsMetadataReviewed} handleStageUpdate={safeExecuteStageTransition}
                handleContinueToReview={handleGeneratePPT}
                handleFinalSubmission={() => safeExecuteStageTransition('Final Approval', 'Moving to final review.')}
                isUpdating={isUpdating} isStageUpdating={isStageUpdating}
                isNoGo={isNoGo}
                isTeamLeadViewOnly={isTeamLeadViewOnly}
                globalStatus={editableData?.global_status?.code}
                editableData={editableData}
                rfpId={rfpId}
            />

            <RFPOverlays
                updateStatus={updateStatus} alertModal={alertModal} setAlertModal={setAlertModal}
                onConfirm={handleConfirmGoNoGo}
                navigate={navigate} showExport={showExport} setShowExport={setShowExport}
                showPPTModal={showPPTModal} setShowPPTModal={setShowPPTModal}
                rfpId={rfpId} editableData={editableData}
                isManager={isManager}
                showVersionDetailsModal={showVersionDetailsModal}
                setShowVersionDetailsModal={setShowVersionDetailsModal}
                selectedVersionData={selectedVersionData} activeSection={activeSection}
                versionsList={versionsList} setRevertTargetVersion={setRevertTargetVersion}
                setShowRevertModal={setShowRevertModal} fetchComparison={fetchComparison}
                handleRenameVersion={handleRenameVersion}
                showComparisonModal={showComparisonModal} setShowComparisonModal={setShowComparisonModal}
                comparisonData={comparisonData} hoveredTabDetails={hoveredTabDetails}
                stripPrefix={stripPrefix} showVersionHistory={showVersionHistory}
                setShowVersionHistory={setShowVersionHistory} activeVersionMenuId={activeVersionMenuId}
                setActiveVersionMenuId={setActiveVersionMenuId} handleViewVersion={handleViewVersion}
                isFetchingVersionDetails={isFetchingVersionDetails}
                setLabelTargetVersion={setLabelTargetVersion}
                setVersionLabelText={setVersionLabelText} setShowLabelModal={setShowLabelModal}
                setIsSelectingCompareFromView={setIsSelectingCompareFromView}
                isSelectingCompareFromView={isSelectingCompareFromView}
                compareSourceVersion={compareSourceVersion} setCompareSourceVersion={setCompareSourceVersion}
                setSelectedVersionData={setSelectedVersionData}
                isFetchingComparison={isFetchingComparison} isGeneratingSlides={isGeneratingSlides}
                isAnyModalOpen={isAnyModalOpen}
            />
        </div>
    );
};

export default RFPAnalysisResult;
